import React, { forwardRef, useEffect, useState } from "react";
import {
  Box,
  Button,
  ButtonGroup,
  Center,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  Spacer,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useDisclosure
} from "@chakra-ui/react";
import _ from "lodash";
import dayjs from "dayjs";
import { Select } from "chakra-react-select";
import { Controller, useForm } from "react-hook-form";
import { CSVLink } from "react-csv";
import DatePicker from "react-datepicker";
import { CalendarIcon } from "@chakra-ui/icons";
import { OriginLocationGroup } from "../../types/stock-operation.type";
import { LoaderComponent } from "../../../core/components/loader.component";
import { PageCardComponent } from "../../../core/components/page-card.component";
import { StockOperationType } from "../../../stock-operation/types/stock-operation.type";
import {
  userCurrentRole,
  canSelectLocation,
  chakraStyles
} from "../../../../utils/common-functions";
import {
  useGetAssignUserLocationsQuery,
  useGetDailyProductReportQuery
} from "../../../../api";
import { ProductOperation } from "../../../../api/type";
import { CmchReportHistoryModalComponent } from "./cmch-report-history-modal.component";
import { CmchDailyProductReportPdfComponent } from "./cmch-daily-product-report-pdf.component";

type DailyReportFormValues = {
  originLocation: OriginLocationGroup;
};

interface CmchDailyProductReportComponentProps {}

export const CmchDailyProductReportComponent: React.FC<
  CmchDailyProductReportComponentProps
> = (props: CmchDailyProductReportComponentProps) => {
  let locationId = localStorage.getItem("location");
  const role = userCurrentRole();

  let initialState = {
    label: "",
    value: Number(locationId)
  };

  const [originLocation, setOriginLocation] =
    useState<OriginLocationGroup>(initialState);

  const [originLocationOptions, setOriginLocationOptions] = useState<
    OriginLocationGroup[]
  >([]);

  const [modalData, setModalData] = useState<ProductOperation>();

  const getLocationQuery = useGetAssignUserLocationsQuery({});

  useEffect(() => {
    if (getLocationQuery.data?.data.locations) {
      const locationsWithValues = getLocationQuery.data?.data.locations.map(
        (location) => {
          return {
            label: location.name,
            value: location.id
          };
        }
      );
      setOriginLocationOptions(locationsWithValues);
    }
  }, [getLocationQuery.data]);

  useEffect(() => {
    if (originLocationOptions.length) {
      setOriginLocation(
        originLocationOptions.find((d) => d.value === initialState.value)!
      );
    }
  }, [initialState.value, originLocationOptions]);

  const handleGetLocation = (data: any) => {
    setOriginLocation(data);
  };

  const [dateStart, setDateStart] = useState(new Date());

  const { data, isLoading } = useGetDailyProductReportQuery({
    data: {
      report: {
        locationId: Number(originLocation?.value),
        startDate: dayjs(dateStart).startOf("day").toISOString(),
        endDate: dayjs(dateStart).endOf("day").toISOString()
      }
    }
  });

  const { control } = useForm<DailyReportFormValues>({});

  const customDateInput = ({ value, onClick, onChange }: any, ref: any) => (
    <Input
      autoComplete="off"
      background="white"
      value={value}
      ref={ref}
      onClick={onClick}
      onChange={onChange}
    />
  );
  customDateInput.displayName = "DateInput";

  const CustomInput = forwardRef(customDateInput);
  function onChangeHandler(value: any) {
    setDateStart(value);
  }
  const icon = <CalendarIcon fontSize="sm" />;

  let allLocation = data?.data.productOperationSummary
    .map((op) => op.operations.map((o) => o.destinationLocation))
    .flat();

  let allUniqueLocation = _.uniqBy(allLocation, "id");
  let allUniqueLocationWithoutNull: { id: number; name: string }[] = [];
  allUniqueLocation.forEach((element) => {
    if (element.id) {
      allUniqueLocationWithoutNull.push(element);
    }
  });

  let headerLocation = [
    {
      name: "P_Id",
      id: Number.MIN_SAFE_INTEGER
    },
    {
      name: "Product Name",
      id: 0
    },
    ...allUniqueLocationWithoutNull,
    {
      name: "Total In",
      id: 99998
    },
    {
      name: "Total Out",
      id: 99999
    }
  ];

  let reportData: any[] = [];

  const findeIndexExist = (id: number) => {
    let indexId = headerLocation.findIndex((TH) => TH.id === id);
    return indexId;
  };

  data?.data.productOperationSummary.forEach((product) => {
    let temp: Array<string | number> = [product.productId];
    temp.push(product.name);
    allUniqueLocationWithoutNull.map((d) => {
      temp.push(0);
    });
    temp.push(0);
    temp.push(0);
    product.operations.map((op) => {
      if (
        op.destinationLocation.id &&
        op.operationType === StockOperationType.TRANSFER &&
        op.destinationLocation.id !== originLocation.value
      ) {
        const indexId = findeIndexExist(op.destinationLocation.id);
        if (indexId !== -1) {
          temp[indexId] = +temp[indexId] + op.quantity;
          temp[temp.length - 1] = +temp[temp.length - 1] + op.quantity;
        }
      } else if (op.operationType === StockOperationType.ADJUSTMENT) {
        if (op.quantity < 0) {
          temp[temp.length - 1] = +temp[temp.length - 1] + op.quantity;
        }
      }
    });
    temp[temp.length - 2] = product.inwardCount;
    reportData.push(temp);
  });

  const csvData = [headerLocation.map((d) => d.name), ...reportData];

  const dailyProductReport = `Daily-Product-Report-${dateStart}.csv`;

  const { isOpen, onOpen, onClose } = useDisclosure();

  const handleOperationDetails = (id: number) => {
    let operationSummary = data?.data.productOperationSummary.find(
      (p) => p.productId === id
    );

    if (operationSummary) {
      setModalData(operationSummary);
    }
  };

  return (
    <Stack>
      <Stack direction={{ base: "column", xl: "row" }} justify="space-between">
        <PageCardComponent>
          <Controller
            control={control}
            name="originLocation"
            render={({
              field: { onChange, onBlur, value, name, ref },
              fieldState: { error }
            }) => (
              <FormControl mb="3" isInvalid={!!error} id="originLocation">
                <FormLabel>Select Location</FormLabel>
                <Select
                  chakraStyles={chakraStyles}
                  name={name}
                  ref={ref}
                  onChange={(location) => handleGetLocation(location)}
                  onBlur={onBlur}
                  isDisabled={!canSelectLocation(role)}
                  value={originLocation}
                  options={originLocationOptions}
                  placeholder="Select location"
                  closeMenuOnSelect={true}
                />
                <FormErrorMessage>{error && error.message}</FormErrorMessage>
              </FormControl>
            )}
          />
        </PageCardComponent>
      </Stack>

      <Stack>
        {modalData && (
          <CmchReportHistoryModalComponent
            isOpen={isOpen}
            onOpen={onOpen}
            onClose={onClose}
            operationSummary={modalData}
          />
        )}
        <Stack direction={{ base: "column", xl: "row" }} width="100%" my="2">
          <Box></Box>
          <Spacer />
          <Box>
            <InputGroup className="dark-theme">
              <DatePicker
                id="dateStartEnd"
                selected={dateStart}
                onChange={(date) => onChangeHandler(date)}
                minDate={new Date("12-20-2022")}
                maxDate={new Date("12-12-2099")}
                dateFormat="dd MMM yyyy"
                className="react-datapicker__input-text"
                placeholderText="Select Date"
                customInput={<CustomInput />}
                showDisabledMonthNavigation
              />
              <InputRightElement color="gray.500" children={icon} />
            </InputGroup>
          </Box>
          <ButtonGroup gap="2">
            {data ? (
              <CSVLink data={csvData} filename={dailyProductReport}>
                <Button colorScheme="blue">Export CSV</Button>
              </CSVLink>
            ) : undefined}
          </ButtonGroup>
          <ButtonGroup gap="2">
            {data && <CmchDailyProductReportPdfComponent printData={data} />}
          </ButtonGroup>
        </Stack>
      </Stack>

      {isLoading ? (
        <LoaderComponent />
      ) : (
        <TableContainer>
          {headerLocation.length && reportData.length > 0 ? (
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  {headerLocation.map((value, key) => {
                    return <Th>{value.name}</Th>;
                  })}
                </Tr>
              </Thead>
              <Tbody>
                {reportData.map((row, key) => {
                  return (
                    <Tr
                      key={key}
                      onClick={() => handleOperationDetails(row[0])}
                    >
                      {row.map((value: any) => {
                        return <Td onClick={onOpen}>{value}</Td>;
                      })}
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          ) : (
            <Center width={"70vw"} height={"30vh"}>
              <Flex>No Data Found !!</Flex>
            </Center>
          )}
        </TableContainer>
      )}
    </Stack>
  );
};
